/**
 * What we accept, and how it will be turned into text. Pure.
 *
 * Two decisions, made in order, and the split is the whole reason there are two:
 *
 * `decideUpload` looks at what the browser *says* — content type, filename, size. It runs on
 * both sides, so a teacher hears "too large" before the bytes leave their machine.
 *
 * `decideContent` looks at what the bytes *are*. Only the Worker has them. A file the browser
 * calls `application/pdf` that does not start with `%PDF-` is refused here, because the
 * declared type is a claim and the signature is the evidence. Nothing enters the pipeline on
 * the claim alone.
 *
 * Two extraction kinds come out the other end: `markdown` means the bytes are already text
 * and go straight through; `converted` means they visit env.AI.toMarkdown() first.
 */

/**
 * A deliberately conservative ceiling. It is not the Worker's request limit — it is a guess
 * at how large a document env.AI.toMarkdown() will convert inside the Worker's budget, and
 * it should be re-tuned against real PDFs. See the plan's Deferred to Implementation.
 */
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/**
 * The most extracted text one analysis will carry forward. Everything past it is dropped and
 * the report says so. The cap bounds the model prompt, the R2 text object and the report
 * body from a single upload — an 8 MB `.md` was going into all three whole.
 */
export const MAX_EXTRACTED_CHARS = 200_000;

export type ExtractionKind = 'markdown' | 'converted';

/** The specific format, decided from the declared type and then verified against the bytes. */
export type UploadFormat =
  | 'markdown'
  | 'text'
  | 'pdf'
  | 'doc'
  | 'docx'
  | 'html'
  | 'png'
  | 'jpeg'
  | 'webp';

export type UploadRejection = 'unsupported_type' | 'too_large' | 'empty' | 'content_mismatch';

export type UploadDecision =
  | { ok: true; kind: ExtractionKind; format: UploadFormat }
  | { ok: false; reason: UploadRejection };

export type ContentDecision =
  | { ok: true }
  | { ok: false; reason: 'content_mismatch' };

const KIND_OF: Readonly<Record<UploadFormat, ExtractionKind>> = {
  markdown: 'markdown',
  text: 'markdown',
  pdf: 'converted',
  doc: 'converted',
  docx: 'converted',
  html: 'converted',
  png: 'converted',
  jpeg: 'converted',
  webp: 'converted',
};

/**
 * What we store and what we tell the converter, regardless of what the browser said. The
 * client's `file.type` is a claim; the object in R2 carries the type we verified. A stored
 * `text/html` type on a bucket that is one day served is a stored XSS, and this is where
 * that door is closed.
 */
export const CANONICAL_CONTENT_TYPE: Readonly<Record<UploadFormat, string>> = {
  markdown: 'text/markdown',
  text: 'text/plain',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  html: 'text/html',
  png: 'image/png',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

/** The extension the converter is handed, so the teacher's filename never has to be. */
export const CANONICAL_EXTENSION: Readonly<Record<UploadFormat, string>> = {
  markdown: 'md',
  text: 'txt',
  pdf: 'pdf',
  doc: 'doc',
  docx: 'docx',
  html: 'html',
  png: 'png',
  jpeg: 'jpg',
  webp: 'webp',
};

const BY_CONTENT_TYPE: Readonly<Record<string, UploadFormat>> = {
  'text/markdown': 'markdown',
  'text/x-markdown': 'markdown',
  'text/plain': 'text',
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'text/html': 'html',
  'image/png': 'png',
  'image/jpeg': 'jpeg',
  'image/webp': 'webp',
};

const BY_EXTENSION: Readonly<Record<string, UploadFormat>> = {
  md: 'markdown',
  markdown: 'markdown',
  txt: 'text',
  pdf: 'pdf',
  doc: 'doc',
  docx: 'docx',
  html: 'html',
  htm: 'html',
  png: 'png',
  jpg: 'jpeg',
  jpeg: 'jpeg',
  webp: 'webp',
};

export interface UploadInput {
  contentType: string;
  filename: string;
  byteLength: number;
}

/** The format a declared content type names, or null. Parameters and case are ignored. */
export function formatOfContentType(contentType: string): UploadFormat | null {
  const mime = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  return BY_CONTENT_TYPE[mime] ?? null;
}

/** The format a filename's extension names, or null. A bare name has no extension. */
export function formatOfFilename(filename: string): UploadFormat | null {
  const extension = filename.split('.').pop()?.toLowerCase() ?? '';
  if (extension === filename.toLowerCase()) return null;
  return BY_EXTENSION[extension] ?? null;
}

export function decideUpload({ contentType, filename, byteLength }: UploadInput): UploadDecision {
  // Size first, before anything looks at the type or the bytes. A 100 MB file should be
  // turned away on its size, not walked to discover what it is.
  if (byteLength > MAX_UPLOAD_BYTES) return { ok: false, reason: 'too_large' };
  if (byteLength <= 0) return { ok: false, reason: 'empty' };

  // Browsers send application/octet-stream (or nothing) for plenty of real files,
  // .md especially. The extension is the only signal left, so use it.
  const format = formatOfContentType(contentType) ?? formatOfFilename(filename);
  if (!format) return { ok: false, reason: 'unsupported_type' };

  return { ok: true, kind: KIND_OF[format], format };
}

// --- the bytes ----------------------------------------------------------------------------

/**
 * The PDF spec lets a header be preceded by junk, and some generators use that. Real files
 * put `%PDF-` in the first few bytes; the allowance covers the ones that do not, without
 * accepting a file that merely mentions PDF somewhere in its body.
 */
const PDF_HEADER_WINDOW = 1024;

const startsWith = (bytes: Uint8Array, signature: number[], offset = 0): boolean =>
  signature.every((expected, i) => bytes[offset + i] === expected);

const ASCII = (text: string): number[] => [...text].map((c) => c.charCodeAt(0));

function hasPdfHeader(bytes: Uint8Array): boolean {
  const marker = ASCII('%PDF-');
  const end = Math.min(bytes.length - marker.length, PDF_HEADER_WINDOW);
  for (let i = 0; i <= end; i += 1) {
    if (startsWith(bytes, marker, i)) return true;
  }
  return false;
}

/**
 * Well-formed UTF-8 per RFC 3629, by hand — core/ has no TextDecoder, and it should not:
 * this is arithmetic on bytes, not I/O. Overlongs, surrogates and anything past U+10FFFF
 * are refused, the same set a fatal decoder refuses.
 */
export function isValidUtf8(bytes: Uint8Array): boolean {
  let i = 0;
  while (i < bytes.length) {
    const b0 = bytes[i]!;
    if (b0 < 0x80) { i += 1; continue; }

    let needed: number;
    let min: number;
    let codePoint: number;
    if (b0 >= 0xc2 && b0 <= 0xdf) { needed = 1; min = 0x80; codePoint = b0 & 0x1f; }
    else if (b0 >= 0xe0 && b0 <= 0xef) { needed = 2; min = 0x800; codePoint = b0 & 0x0f; }
    else if (b0 >= 0xf0 && b0 <= 0xf4) { needed = 3; min = 0x10000; codePoint = b0 & 0x07; }
    else return false;

    for (let k = 1; k <= needed; k += 1) {
      const b = bytes[i + k];
      if (b === undefined || (b & 0xc0) !== 0x80) return false;
      codePoint = (codePoint << 6) | (b & 0x3f);
    }
    if (codePoint < min) return false;
    if (codePoint > 0x10ffff) return false;
    if (codePoint >= 0xd800 && codePoint <= 0xdfff) return false;
    i += needed + 1;
  }
  return true;
}

/**
 * Text is anything that is well-formed UTF-8 and carries no NUL. That refuses a binary
 * renamed to `.md`, and it refuses UTF-16 exports too — a trade the copy explains ("save as
 * UTF-8"), and a much smaller population than mislabelled binaries.
 */
function isText(bytes: Uint8Array): boolean {
  return !bytes.includes(0) && isValidUtf8(bytes);
}

const SIGNATURE_CHECK: Readonly<Record<UploadFormat, (bytes: Uint8Array) => boolean>> = {
  markdown: isText,
  text: isText,
  html: isText,
  pdf: hasPdfHeader,
  // DOCX is a zip; the local-file header every zip begins with.
  docx: (bytes) => startsWith(bytes, [0x50, 0x4b, 0x03, 0x04]),
  // Legacy .doc is an OLE compound file.
  doc: (bytes) => startsWith(bytes, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]),
  png: (bytes) => startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
  jpeg: (bytes) => startsWith(bytes, [0xff, 0xd8, 0xff]),
  webp: (bytes) => startsWith(bytes, ASCII('RIFF')) && startsWith(bytes, ASCII('WEBP'), 8),
};

/**
 * Do the bytes match the format the declaration led us to expect? Only the Worker can ask —
 * the browser-side check has metadata, not content.
 */
export function decideContent(format: UploadFormat, bytes: Uint8Array): ContentDecision {
  return SIGNATURE_CHECK[format](bytes) ? { ok: true } : { ok: false, reason: 'content_mismatch' };
}

// --- the extracted text -------------------------------------------------------------------

export interface ClampedText {
  markdown: string;
  /** True when something was dropped. The report has to say so. */
  truncated: boolean;
}

/** Cut at the last line break inside the cap, so a truncated plan ends on a whole line. */
export function clampExtractedText(markdown: string, max: number = MAX_EXTRACTED_CHARS): ClampedText {
  if (markdown.length <= max) return { markdown, truncated: false };

  const clipped = markdown.slice(0, Math.max(0, max));
  const lastBreak = clipped.lastIndexOf('\n');
  const kept = lastBreak > max / 2 ? clipped.slice(0, lastBreak) : clipped;
  return { markdown: kept.trimEnd(), truncated: true };
}
